import React from 'react';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import { BookingEntity } from 'Models/Entities';
import useDeviceDetect from 'Hooks/useDeviceDetect';
import { LinkedBookingPricingResult } from 'Services/Api/_HumanWritten/PricingService/PricingResult';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { reportHandledError } from 'Util/_HumanWritten/Observability/observability';
import alertToast from 'Util/ToastifyUtils';
import { Journey } from 'Global/Journey/Types/Journey';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { useBookingWizardPricingCacheStore } from '../../../BookingWizardPricingCacheStore';
import { BookingWizardData } from '../../../BookingWizardData/BaseBookingWizardData';
import { getBookingTypeFromTabSelected } from '../../../BookingWizardHelpers';
import { BookingWizardCartFields } from '../../../BookingWizardTypes';
import { tripChoiceModal, TripChoiceOption } from './TripChoiceModal';
import { LegDate, LegDirection, LegGroups, LegRoute, LegTotal, useClassLeg, useLegText } from './ClassLegParts';
import { useStopById } from 'Global/JourneyElements/Hooks/useStopsById';

export interface ClassBookingSummaryCardProps {
	wizardData?: BookingWizardData;
	booking: BookingEntity;
	cartFields: BookingWizardCartFields;
	count: number;
	bookingInfo?: LinkedBookingPricingResult;
	/**
	 * The cart (6a / 6b), the wizard's sidebar (4a), or check-in's review page — which is the cart's
	 * layout without the cart's Edit and Remove, since those go to booking-wizard pages and check-in
	 * edits a booking through its own form.
	 */
	location: 'cart' | 'sidebar' | 'check-in';
	isAlteration: boolean;
	onRemoveBooking: (
		bookingId: string,
		removeAssociatedBooking: boolean,
		departureBooking: boolean,
	) => Promise<boolean>;
	onEditBooking: (
		cartFields: BookingWizardCartFields,
		departureBooking: boolean,
		bookingEntity: BookingEntity,
		bothTrips?: boolean,
	) => Promise<void>;
}

/** `Walk-on Booking 1` / `Walk-on Update 1`. */
export function BookingName({
	tabSelected,
	isAlteration,
	suffix,
}: {
	tabSelected: BookingWizardCartFields['wizardData']['tabSelected'];
	isAlteration: boolean;
	suffix?: React.ReactNode;
}) {
	const type = getBookingTypeFromTabSelected(tabSelected);
	const name = type.charAt(0).toUpperCase() + type.slice(1);

	return (
		<>
			{isAlteration ? (
				<FormattedMessage
					id="class-booking-summary__update-name"
					defaultMessage="{type} Update"
					values={{ type: name }}
				/>
			) : (
				<FormattedMessage
					id="class-booking-summary__booking-name"
					defaultMessage="{type} Booking"
					values={{ type: name }}
				/>
			)}
			{suffix !== undefined && <> {suffix}</>}
		</>
	);
}

/** `Return trip` / `One way`. */
export function TripType({ oneWay }: { oneWay: boolean }) {
	return oneWay ? (
		<FormattedMessage id="class-booking-summary__one-way" defaultMessage="One way" />
	) : (
		<FormattedMessage id="class-booking-summary__return-trip" defaultMessage="Return trip" />
	);
}

interface LegProps {
	journey: Journey;
	departing: boolean;
	bookingInfo: LinkedBookingPricingResult['departureBooking'] | null | undefined;
	isAlteration: boolean;
}

/** One leg in the cart: its heading beside its lines on web (6a), above them on a phone (6b). */
function CartLeg({ journey, departing, bookingInfo, isAlteration }: LegProps) {
	const leg = useClassLeg(bookingInfo ?? undefined, isAlteration);

	return (
		<div
			className="class-booking-summary__cart-leg"
			data-test-id={`class-booking-summary-leg-${departing ? 'departing' : 'returning'}`}
		>
			<div className="class-booking-summary__cart-leg-heading">
				<div className="class-booking-summary__eyebrow">
					<LegDirection departing={departing} />
				</div>
				<LegRoute journey={journey} className="class-booking-summary__cart-leg-route" />
				<LegDate journey={journey} />
			</div>
			<div className="class-booking-summary__cart-leg-lines">
				{leg && (
					<>
						<LegGroups leg={leg} isAlteration={isAlteration} divided />
						<LegTotal
							leg={leg}
							departing={departing}
							isAlteration={isAlteration}
							className="class-booking-summary__cart-leg-total"
						/>
					</>
				)}
			</div>
		</div>
	);
}

/**
 * One leg in the sidebar (4a), laid out as the cart does on a phone: its heading on a tinted band,
 * then its lines and total.
 */
function SidebarLeg({ journey, departing, bookingInfo, isAlteration }: LegProps) {
	const leg = useClassLeg(bookingInfo ?? undefined, isAlteration);

	return (
		<div className="class-booking-summary__sidebar-leg">
			<div className="class-booking-summary__sidebar-leg-heading">
				<div className="class-booking-summary__eyebrow">
					<LegDirection departing={departing} />
				</div>
				<LegRoute journey={journey} className="class-booking-summary__sidebar-leg-route" />
				<LegDate journey={journey} />
			</div>
			{leg && (
				<div className="class-booking-summary__sidebar-leg-lines">
					<LegGroups leg={leg} isAlteration={isAlteration} divided />
					<LegTotal
						leg={leg}
						departing={departing}
						isAlteration={isAlteration}
						className="class-booking-summary__sidebar-leg-total"
					/>
				</div>
			)}
		</div>
	);
}

/** A booking in the sidebar that is not the one being built: its name, route and price only. */
function CollapsedSidebarBooking({
	cartFields,
	count,
	price,
	isAlteration,
}: {
	cartFields: BookingWizardCartFields;
	count: number;
	price: number;
	isAlteration: boolean;
}) {
	const journey = cartFields.departingJourney;
	const from = useStopById(journey?.startStopId ?? '')?.location.name;
	const to = useStopById(journey?.endStopId ?? '')?.location.name;

	return (
		<div className="class-booking-summary__collapsed" data-test-id="class-booking-summary-collapsed-booking">
			<div>
				<div className="class-booking-summary__collapsed-name">
					<BookingName
						tabSelected={cartFields.wizardData.tabSelected}
						isAlteration={isAlteration}
						suffix={count + 1}
					/>
				</div>
				<div className="class-booking-summary__collapsed-route">
					{from} → {to} · <TripType oneWay={cartFields.wizardData.tripType === 'one way'} />
				</div>
			</div>
			<span className="class-booking-summary__collapsed-price">{formatPriceDisplay(price, isAlteration)}</span>
		</div>
	);
}

function ClassBookingSummaryCard({
	wizardData,
	booking,
	cartFields,
	count,
	bookingInfo,
	location,
	isAlteration,
	onRemoveBooking,
	onEditBooking,
}: ClassBookingSummaryCardProps) {
	const { getBookingPrice } = useBookingWizardPricingCacheStore();
	const { isMobileBookingWizard } = useDeviceDetect();

	const departingLeg = useClassLeg(bookingInfo?.departureBooking, isAlteration);
	const returningLeg = useClassLeg(bookingInfo?.returnBooking ?? undefined, isAlteration);

	const isCurrentBooking = wizardData?.bookingToEdit === cartFields.wizardData.bookingToEdit;
	const oneWay = cartFields.wizardData.tripType === 'one way';

	// Same rule as TripBookingSummaryCard: a return leg is shown once it has a ticket, or while the
	// booking being built is a return one that has picked its returning sailing.
	const showReturn =
		!!cartFields.returningJourney &&
		(cartFields.wizardData.returningTicketId !== '' ||
			(cartFields.wizardData.tripType === 'return' &&
				wizardData?.returningTicketId !== '' &&
				wizardData?.returningTicketId !== undefined));

	const departingText = useLegText(cartFields.departingJourney);
	const returningText = useLegText(showReturn ? cartFields.returningJourney : undefined);

	// Design 12p: one Edit and one Remove for the booking. On a return booking a single modal asks which
	// sailings they apply to; on a one-way booking there is only one to choose.
	const tripOptions = (): TripChoiceOption[] => {
		const departing: TripChoiceOption = {
			value: 'departing',
			title: <LegDirection departing />,
			routes: [departingText],
		};
		if (!showReturn) {
			return [departing];
		}
		return [
			{
				value: 'both',
				title: <FormattedMessage id="class-trip-choice__both" defaultMessage="Both trips" />,
				routes: [departingText, returningText],
			},
			departing,
			{ value: 'returning', title: <LegDirection departing={false} />, routes: [returningText] },
		];
	};

	const handleEdit = async () => {
		if (!showReturn) {
			await onEditBooking(cartFields, true, booking, true);
			return;
		}
		const choice = await tripChoiceModal('edit', tripOptions());
		if (choice) {
			await onEditBooking(cartFields, choice !== 'returning', booking, choice === 'both');
		}
	};

	const handleRemove = async () => {
		try {
			const choice = await tripChoiceModal('remove', tripOptions());
			if (choice) {
				await onRemoveBooking(
					cartFields.wizardData.bookingToEdit ?? '',
					!showReturn || choice === 'both',
					choice !== 'returning',
				);
			}
		} catch (e) {
			reportHandledError(e, { event: 'cart_remove_booking_failed' });
			alertToast('There was an error removing this booking.', 'error', 'Failed to remove booking');
		}
	};

	if (location === 'sidebar') {
		if (!isCurrentBooking) {
			return (
				<CollapsedSidebarBooking
					cartFields={cartFields}
					count={count}
					price={getBookingPrice(booking.id)}
					isAlteration={isAlteration}
				/>
			);
		}

		return (
			<div className="class-booking-summary__card" data-test-id="class-booking-summary-current-booking">
				<div className="class-booking-summary__sidebar-card-header">
					<span>
						<BookingName
							tabSelected={cartFields.wizardData.tabSelected}
							isAlteration={isAlteration}
							suffix={count + 1}
						/>
					</span>
					<span>
						<TripType oneWay={oneWay} />
					</span>
				</div>
				{cartFields.departingJourney && (
					<SidebarLeg
						journey={cartFields.departingJourney}
						departing
						bookingInfo={bookingInfo?.departureBooking}
						isAlteration={isAlteration}
					/>
				)}
				{showReturn && (
					<SidebarLeg
						journey={cartFields.returningJourney!}
						departing={false}
						bookingInfo={bookingInfo?.returnBooking}
						isAlteration={isAlteration}
					/>
				)}
			</div>
		);
	}

	const bookingTotal = (departingLeg?.total ?? 0) + (showReturn ? (returningLeg?.total ?? 0) : 0);
	const showActions = !isAlteration && location !== 'check-in';

	return (
		<div
			className={classNames('class-booking-summary__card', {
				'class-booking-summary__card--mobile': isMobileBookingWizard,
			})}
			data-test-id="class-booking-summary-booking"
		>
			<div className="class-booking-summary__cart-card-header">
				<div className="class-booking-summary__cart-card-title">
					<span className="class-booking-summary__cart-card-name">
						<BookingName
							tabSelected={cartFields.wizardData.tabSelected}
							isAlteration={isAlteration}
							suffix={count + 1}
						/>
					</span>
					<span className="class-booking-summary__trip-type">
						<TripType oneWay={oneWay || !showReturn} />
					</span>
				</div>
				{showActions && (
					<div className="class-booking-summary__actions">
						<Button
							className="class-booking-summary__edit"
							colors={Colors.Grey}
							display={Display.Outline}
							onClick={handleEdit}
							newStyles
							buttonProps={{ 'data-test-id': 'class-booking-summary-edit' } as any}
						>
							<FormattedMessage id="class-booking-summary__edit" defaultMessage="Edit" />
						</Button>
						<Button
							className="class-booking-summary__remove"
							colors={Colors.Red}
							display={Display.Outline}
							onClick={handleRemove}
							newStyles
							buttonProps={{ 'data-test-id': 'class-booking-summary-remove' } as any}
						>
							<FormattedMessage id="class-booking-summary__remove" defaultMessage="Remove" />
						</Button>
					</div>
				)}
			</div>
			{cartFields.departingJourney && (
				<CartLeg
					journey={cartFields.departingJourney}
					departing
					bookingInfo={bookingInfo?.departureBooking}
					isAlteration={isAlteration}
				/>
			)}
			{showReturn && (
				<CartLeg
					journey={cartFields.returningJourney!}
					departing={false}
					bookingInfo={bookingInfo?.returnBooking}
					isAlteration={isAlteration}
				/>
			)}
			{/* On a one-way booking this would only repeat `Departing total`. */}
			{showReturn && (
				<div
					className="class-booking-summary__booking-total"
					data-test-id="class-booking-summary-booking-total"
				>
					<span>
						<FormattedMessage id="class-booking-summary__booking-total" defaultMessage="Booking total" />
					</span>
					<span>{formatPriceDisplay(bookingTotal, isAlteration)}</span>
				</div>
			)}
		</div>
	);
}

export default observer(ClassBookingSummaryCard);
